import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Sparkles, Briefcase, Crown } from 'lucide-react';
import { PLANS, getPlansForPersona } from '../lib/plans';

/**
 * PlansSection — Premium pricing block displayed inline on the Landing page.
 * Renders all 5 official GetSpark plans (toggle Empreendedor / Investidor).
 * CTAs send visitors to /auth carrying their plan + role intent.
 *
 * NOTE: This is a Landing section. It DOES NOT replace any existing section
 * and is mounted between NumbersSection and CTASection by App.js.
 */
const PlansSection = () => {
  const [persona, setPersona] = useState('investor');
  const visiblePlans = useMemo(() => getPlansForPersona(persona), [persona]);

  const handleSelect = (plan) => {
    const params = new URLSearchParams();
    params.set('signup', '1');
    params.set('role', plan.persona);
    if (plan.tier !== 'free') params.set('plan', plan.id);
    window.location.href = `/auth?${params.toString()}`;
  };

  return (
    <section
      id="planos"
      data-testid="landing-plans-section"
      className="relative py-20 sm:py-28 px-4 sm:px-6 scroll-mt-24"
    >
      {/* Soft background ambience */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -left-32 w-80 h-80 rounded-full bg-[#4facfe]/[0.07] blur-[120px]" />
        <div className="absolute bottom-1/4 -right-32 w-80 h-80 rounded-full bg-[#ffa726]/[0.07] blur-[120px]" />
      </div>

      <div className="relative max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-12"
        >
          <span className="eyebrow inline-flex items-center gap-2">
            <Sparkles size={12} className="text-[#ffa726]" />
            Planos GetSpark
          </span>
          <h2 className="mt-4 heading-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
            Escolha o plano que <span className="gradient-text">acelera sua Spark</span>.
          </h2>
          <p className="mt-4 text-sm sm:text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Comece grátis hoje. Faça upgrade quando precisar de mais. Sem fidelidade, cancele quando quiser.
            Pagamento via cartão ou PIX em BRL.
          </p>
        </motion.div>

        {/* Persona toggle */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex justify-center mb-10"
        >
          <div className="inline-flex items-center gap-1 p-1 rounded-2xl bg-white/[0.04] border border-white/[0.06] backdrop-blur-xl">
            <button
              onClick={() => setPersona('entrepreneur')}
              data-testid="landing-plans-toggle-entrepreneur"
              className={`px-5 sm:px-6 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center gap-2 ${
                persona === 'entrepreneur'
                  ? 'bg-gradient-to-r from-[#4facfe] to-[#00f2fe] text-white shadow-[0_4px_20px_rgba(79,172,254,0.35)]'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Briefcase size={14} /> Empreendedor
            </button>
            <button
              onClick={() => setPersona('investor')}
              data-testid="landing-plans-toggle-investor"
              className={`px-5 sm:px-6 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center gap-2 ${
                persona === 'investor'
                  ? 'bg-gradient-to-r from-[#ffa726] to-[#ff7043] text-white shadow-[0_4px_20px_rgba(255,167,38,0.35)]'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Crown size={14} /> Investidor
            </button>
          </div>
        </motion.div>

        {/* Plans grid */}
        <div className={`grid gap-5 sm:gap-6 ${
          visiblePlans.length === 2 ? 'sm:grid-cols-2 max-w-3xl mx-auto' : 'sm:grid-cols-2 lg:grid-cols-3'
        }`}>
          {visiblePlans.map((p, i) => {
            const isElite = p.tier === 'elite';
            const isPopular = p.id === 'investor_pro' || p.id === 'entrepreneur_pro';
            const isFree = p.tier === 'free';

            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-30px' }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className={`relative glass-card rounded-3xl p-6 sm:p-7 flex flex-col ${
                  isElite ? 'border-[#ffa726]/40 shadow-[0_18px_60px_rgba(255,167,38,0.15)]' : ''
                } ${isPopular ? 'sm:scale-[1.02] border-[#4facfe]/30' : ''}`}
                data-testid={`landing-plan-card-${p.id}`}
              >
                {isPopular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1 rounded-full bg-gradient-to-r from-[#4facfe] to-[#00f2fe] text-white whitespace-nowrap shadow-[0_4px_14px_rgba(79,172,254,0.4)]">
                    Mais escolhido
                  </span>
                )}
                {isElite && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1 rounded-full bg-gradient-to-r from-[#ffa726] to-[#ff7043] text-white whitespace-nowrap shadow-[0_4px_14px_rgba(255,167,38,0.4)]">
                    Premium
                  </span>
                )}

                <div className="flex items-center gap-2 mb-1">
                  <h3 className="heading-display text-xl font-bold text-white">{p.name}</h3>
                </div>
                <p className="text-xs text-slate-400 mb-5">{p.tagline}</p>

                <div className="flex items-baseline gap-1.5 mb-6">
                  <span className="heading-display text-4xl sm:text-[2.5rem] font-bold gradient-text">
                    {p.price_label}
                  </span>
                  <span className="text-xs text-slate-500">{p.period_label}</span>
                </div>

                <ul className="space-y-2.5 mb-7 flex-1" data-testid={`landing-plan-features-${p.id}`}>
                  {p.features.map((f, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 text-sm text-slate-200">
                      <span className="w-5 h-5 rounded-full bg-[#4facfe]/15 text-[#4facfe] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check size={11} strokeWidth={3} />
                      </span>
                      <span className="leading-snug">{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelect(p)}
                  data-testid={`landing-plan-cta-${p.id}`}
                  className={`w-full rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2 transition-all group/btn ${
                    isElite
                      ? 'btn-gold'
                      : isPopular
                      ? 'btn-primary'
                      : isFree
                      ? 'glass-card hover:bg-white/[0.06] text-white border-white/[0.08]'
                      : 'glass-card hover:bg-white/[0.06] text-white'
                  }`}
                >
                  {isFree ? 'Começar Grátis' : `Assinar ${p.name.split(' ').slice(-1)[0]}`}
                  <ArrowRight size={14} className="group-hover/btn:translate-x-0.5 transition-transform" />
                </button>
              </motion.div>
            );
          })}
        </div>

        <p className="text-center text-[11px] text-slate-500 mt-10">
          Pagamento processado pelo Stripe. Aceitamos cartão e PIX em BRL.
        </p>
      </div>
    </section>
  );
};

export default PlansSection;
